import { FC, useEffect, useState } from "react";
import { IStudent, IStudentClassInfo } from "../../../models/students.model";
import StudentName from "./Name";
import { Select, Table, TableColumnsType } from "antd";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import useAxios from "../../../hooks/useAxios";
import { IGetStudentArticles, IStudentArticle } from "../../../models/articles.model";
import { getStudentArticles } from "../../../api/articles.api";
import config from "../../../lib/config";
import moment from "moment";
import Button from "../../Button";
import Pagination from "../../common/Pagination";
import useLoader from "../../../hooks/useLoader";
import { IGetJwtToken } from "../../../models/auth.model";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/rootReducer";
import { getJwtToken } from "../../../api/auth.api";
import { getCanbOnUrl } from "../../../lib/util";

const Block = styled.div`
  .select-wrap {
    margin: 15px 0;
    display: flex;
    justify-content: flex-end;
  }
`;

interface IProps {
  student?: IStudent;
  classInfo?: IStudentClassInfo[];
};

const StudentArticlePane: FC<IProps> = ({
  student,
  classInfo = [],
}) => {
  const { t } = useTranslation();
  const { auth } = useSelector((state: RootState) => state.auth);  
  const { data: res, isLoading, error, execute } = useAxios<IGetStudentArticles>();
  const { data: res2, isLoading: isLoading2, error: error2, execute: execute2 } = useAxios<IGetJwtToken>();
  useLoader(
    isLoading || isLoading2,
    [error, error2],
  );

  const [pageNo, setPageNo] = useState(1);
  const [isFirstRun, setIsFirstRun] = useState(true);
  const [classCode, setClassCode] = useState<string>();

  useEffect(() => {
    if (getMiddleClasses().length) {
      setClassCode(getMiddleClasses().at(0)!.classCode);
    } else {
      setClassCode(undefined);
    }
  }, [classInfo]);

  useEffect(() => {
    setPageNo(1);
    doGetStudentArticles(1);
  }, [student, classCode]);

  useEffect(() => {
    if (isFirstRun) {
      setIsFirstRun(false);
      return;
    }
    doGetStudentArticles();
  }, [pageNo]);

  useEffect(() => {
    if (student) {
      execute2(getJwtToken({
        adminMemCode: auth.memberCode,
        studentMemCode: student.studentCode,
      }));
    }
  }, [student]);

  const doGetStudentArticles = (_pageNo?: number) => {
    if (student?.studentCode && classCode !== undefined) {
      execute(getStudentArticles({
        pageNo: _pageNo || pageNo,
        perPage: config.middlePageSize,
        memberCode: student.studentCode,
        classCode,
      }));  
    }
  };

  const getMiddleClasses = () => {
    return classInfo.filter(item => item.eduStepCode === 'MD');
  };

  const onOpenWindow = (blogPostCode: number) => {
    window.open(
      `${getCanbOnUrl()}/#/viewer/blog/${blogPostCode}?token=${res2?.data}`,
      '_blank',
      'width=1080, height=800, fullscreen=no, menubar=no, status=no, toolbar=no, titlebar=no, location=no, scrollbar=no',
    );
  };

  const columns: TableColumnsType<IStudentArticle> = [
    {
      title: 'No.',
      dataIndex: 'rowNo',
      width: '5%',
      sorter: (a, b) => a.rowNo - b.rowNo,
    },
    {
      title: 'Post Title',
      dataIndex: 'title',
      width: '55%',
      ellipsis: true,
      sorter: (a, b) => a.title?.localeCompare(b.title),
    },
    {
      title: 'Type of Writing',
      dataIndex: 'abbrEn',
      width: '20%',
      ellipsis: true,
      sorter: (a, b) => a.abbrEn?.localeCompare(b.abbrEn),
    },
    {
      title: t('student.student.62'),
      dataIndex: 'postDate',
      width: '10%',
      sorter: (a, b) => a.postDate?.localeCompare(b.postDate),
      render: value => moment(value).format('YYYY-MM-DD'),
    },
    {
      title: t('student.student.63'),
      dataIndex: 'blogPostCode',
      width: '10%',
      render: value => (
        <div className='comm-btn__area justify-content-center'>
          <Button
            className="btn-basic small mint"
            onClick={() => onOpenWindow(value)}
            text={t('student.student.64')}
          />
        </div>
      )
    },
  ];

  return (
    <Block>
      <StudentName
        student={student}
      />
      <div className="comm-bx__space">
        <div className='comm-btn__area right'>
          <Select
            className="w-210p"
            placeholder={t('student.student.column23')}
            value={classCode}
            onChange={value => setClassCode(value)}
          >
            {getMiddleClasses().length && (
              <Select.Option value="">
                {t('common.text.all')}
              </Select.Option>
            )}
            {getMiddleClasses().map(item => (
              <Select.Option
                key={item.classCode}
                value={item.classCode}
              >
                ({item.semesterName}) {item.className}
              </Select.Option>
            ))}
          </Select>
        </div>
      </div>
      {res?.data && (
        <Table
          className='comm-table__area'
          columns={columns}
          dataSource={res?.data}
          pagination={{
            current: pageNo,
            pageSize: config.middlePageSize,
          }}
        />
      )}
      <div className="comm-bx__between">
        <div className="common-paging center">
          <Pagination
            page={pageNo}
            setPage={setPageNo}
            total={res?.total}
            pageSize={config.middlePageSize}
          />
        </div>
      </div>
    </Block>
  );
};

export default StudentArticlePane;